/**
 * Wire-mapping for the governance admin-role assignment panel (OIDCPAM
 * Gate-6 Phase 6a — GovernanceRoles.tsx).
 *
 * `AdminGovernanceListRolesResp.assignments` is typed `Array<Record<string,
 * unknown>>` in the generated client — an opaque/untyped response, same
 * pattern as governanceDualAuthMapping.ts. The generated client's fromWire()
 * camelCases every response key, including inside opaque array items.
 *
 * LATENT BUG FIXED BY THIS MAPPING (flagged in the shared Gate-6 doc — this
 * is one of the ~6 live prod bugs it warns about): the pre-migration
 * GovernanceRoles.tsx did
 * `setAssignments((data.assignments ?? []) as unknown as AdminRoleAssignment[])`
 * with ZERO key conversion, where the local `AdminRoleAssignment` type
 * (../api/types.ts) is snake_case (subject_id, admin_role, assigned_at). The
 * real backend struct (pam_admin_role_assignment.go#AdminRoleAssignment) is
 * `{ SubjectID string \`json:"subject_id"\`, RoleKind AdminRoleKind
 * \`json:"role_kind"\`, GrantedAt time.Time \`json:"granted_at"\` }` — note
 * the field is `role_kind`, NOT `admin_role` as ../api/types.ts's hand-written
 * (and apparently stale) doc comment claims. After fromWire camelCases the
 * response, the actual runtime keys are `subjectId`/`roleKind`/`grantedAt` —
 * none of `a.subject_id`, `a.admin_role`, `a.assigned_at` ever match, so
 * every column silently rendered blank/undefined in production. This mapping
 * reads the real camelCase keys and maps roleKind -> the component's
 * `roleName` prop field.
 *
 * SEPARATE BACKEND LIMITATION (not a frontend bug, out of scope for this
 * migration): `AdminGovernanceListRoles` (admin_governance_server.go) always
 * returns `Assignments: []any{}` today — an empty stub, with the response
 * `note` field literally stating "role assignments are currently managed via
 * token claims". So this list is always empty regardless of the mapping
 * above; the mapping exists for forward-compatibility/correctness once the
 * backend actually populates it.
 */
import type {
  AdminRoleAssignment as IamAdminRoleAssignment,
} from '@devnw/cyber-iam/iam/types'

/** The real (camelCase) runtime shape of one AdminGovernanceListRolesResp assignment. */
export interface WireAdminRoleAssignment {
  subjectId: string
  roleKind: string
  grantedAt: string
}

/** HOST-COMPUTED display formatting — the library never parses dates. */
function fmtDate(iso?: string): string | undefined {
  return iso ? new Date(iso).toLocaleString() : undefined
}

export function toIamAdminRoleAssignment(
  w: WireAdminRoleAssignment
): IamAdminRoleAssignment {
  return {
    subjectId: w.subjectId,
    roleName: w.roleKind,
    assignedAt: fmtDate(w.grantedAt),
  }
}
